/*
 * NewPin.tsx
 *
 * page for entering desired new pin.
 *
 * Created by Nick Perry on 9/2/22.
 *
 * dha-login
 *
 * Copyright © 2009-2022 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2022 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */
import type { Dispatch, SetStateAction } from 'react';
import { useContext } from 'react';

import { Dialog } from '@headlessui/react';
import { encrypt, decrypt } from 'dha-encryption';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import * as yup from 'yup';

import { themeAtom } from '../../atoms/themeAtom';
import { CustomInput } from '../../components/CustomInput';
import type { IPinResetStatusState } from '../../controllers/LoginController';
import { LoginDatabaseContext } from '../../providers/LoginDatabaseProvider';

interface INewPinProps {
  setPinResetStatus: Dispatch<SetStateAction<IPinResetStatusState>>;
}

const NewPin = ({ setPinResetStatus }: INewPinProps) => {
  const { DHA_Key, database } = useContext(LoginDatabaseContext);
  const theme = useAtomValue(themeAtom);

  const validationSchema = yup.object({
    pin: yup
      .string()
      .required('Enter a valid pin')
      .min(6, 'Pin must be at least 6 characters')
      .matches(new RegExp('^\\d+$'), 'Pin must only contain numbers')
      .trim(),
  });

  return (
    <Dialog
      open={true}
      as="div"
      className="relative z-10"
      onClose={(boolie) => {
        if (boolie) console.log(''); // this is the peak of programming & human ingenuity.
      }}
    >
      <div className={`fixed inset-0 ${theme.modalBackground}`} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <Dialog.Panel
            className={`h-full max-h-fit w-full overflow-hidden rounded-2xl ${theme.cardColor} p-6 text-center align-middle shadow-xl transition-all md:max-w-sm`}
          >
            <Dialog.Title as="h3" className={`text-lg font-medium leading-6 ${theme.textColor}`}>
              Set your new PIN:
            </Dialog.Title>

            <Formik
              initialValues={{
                pin: '',
              }}
              onSubmit={(enteredValues) => {
                if (database !== null) {
                  /* 
                    Since userProfile data is stored as an encrypted ArrayBuffer in indexDB,
                    we can't rely only on Dexie's native (and straightforward) .update() function.
                    We need to retrieve, decrypt, update, and re-encrypt the updated data before again storing it in indexDB.
                  */
                  const updateTable = async () => {
                    // necessary check due to TypeScript restrictions
                    if (DHA_Key === undefined) {
                      return;
                    }

                    try {
                      // acquire encrypted ArrayBuffer from indexDB
                      const encryptedUser = await database.userProfile.get('primaryUser');
                      if (encryptedUser !== undefined) {
                        // decrypt encrypted ArrayBuffer from indexDB
                        const decryptedUserString = await decrypt(
                          encryptedUser.userProfile,
                          DHA_Key
                        );
                        const decryptedObject = JSON.parse(decryptedUserString);

                        // update userProfile with new pin
                        const updatedObject = { ...decryptedObject, pin: enteredValues.pin };

                        // re-encrypt the updated object for storage
                        const updatedEncryptedUser = await encrypt(
                          JSON.stringify(updatedObject),
                          DHA_Key
                        );

                        // update the table with the new object
                        await database.userProfile.update('primaryUser', {
                          userProfile: updatedEncryptedUser,
                        });

                        // update PinReset status & navigate home
                        setPinResetStatus((prevState: IPinResetStatusState) => {
                          return {
                            ...prevState,
                            answeringSecurityQuestions: false,
                            makingNewPin: false,
                          };
                        });
                      }
                    } catch (err) {
                      console.log(err);
                    }
                  };
                  updateTable().catch((e) => console.error(e));
                }
              }}
              validationSchema={validationSchema}
            >
              {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                  <div id="form-area" className="h-28">
                    <div className="mt-8 flex flex-col items-center">
                      <CustomInput
                        type="password"
                        name="pin"
                        value={formikProps.values.pin}
                        isError={!!formikProps.errors.pin}
                        isTouched={formikProps.touched.pin}
                        onChange={formikProps.handleChange}
                        maxLength={6}
                        placeholder="enter new PIN..."
                      />

                      {formikProps.errors.pin && formikProps.touched.pin ? (
                        <div className="text-base font-medium text-rose-500" id="feedback">
                          {formikProps.errors.pin}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="">
                    <button
                      type="submit"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium ${theme.buttonShadow} ${theme.buttonColor} ${theme.buttonTextColor} hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 mt-5`}
                    >
                      Set PIN
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default NewPin;
